import { datadogLogs } from '@datadog/browser-logs';
import { useEffect, useState } from 'react';
import { isAndroid, isIOS } from 'react-device-detect';
import { MAIN_SERVICE_PATHS } from '@/constants/navigation';
import { CouponIssueReason } from '../../api/user/dto/coupon-issue-reason.enum';
import type { IssuedCoupon } from '../../api/user/dto/response/app-onboarding-benefit.res.dto';
import useAppInstallBenefitEligibilityQuery from '../../api/user/hooks/useAppInstallBenefitEligibilityQuery';
import useIssueAppInstallBenefitMutation from '../../api/user/hooks/useIssueAppInstallBenefitMutation';
import { TimeConstants } from '../../constants/time';
import { AutoShowModalId } from '../../store/useAutoShowModalStore';
import { LOCAL_STORAGE_KEYS } from '../../utils/common/local-storage-keys';
import { ReactNativeGtm } from '../../utils/gtm/react-native/ReactNativeGtm';
import { redirectApp } from '../../utils/hook/react-native';
import useMeQuery from '../../utils/hook/react-query/user/useMeQuery';
import { useAutoShowModalSlot } from '../../utils/hook/useAutoShowModalSlot';
import useClientSide from '../../utils/hook/useClientSide';
import { useModalCooldown } from '../../utils/hook/useModalCooldown';
import { usePathnameMethod } from '../../utils/hook/usePathnameMethod';
import { isReactNative } from '../../utils/react-native';

/** 모달 닫기·앱 이동 시 재노출 쿨다운 기간 */
const CLOSE_COOLDOWN_DURATION_MS = TimeConstants.DAY;

/** "7일 동안 보지 않기" 클릭 시 재노출 쿨다운 기간 */
const NEVER_SHOW_COOLDOWN_DURATION_MS = 7 * TimeConstants.DAY;

/** 웹→앱 모달 노출 단계 */
export enum WebToAppModalStep {
  /** 설치 유도 + 쿠폰 받기 */
  INSTALL = 'INSTALL',

  /** 쿠폰 발급 완료 안내 */
  COUPON_ISSUED = 'COUPON_ISSUED',
}

/**
 * 웹→앱 설치 유도 모달의 노출 조건 판정과 2단계(설치 유도 → 쿠폰 발급 완료) 전환을 관리한다.
 *
 * - 노출 조건(AND): 모바일 웹(OS 기준) + 로그인 + 기존 띠배너 6개 탭 경로 + 앱 웹뷰 제외 + BE 노출 자격(eligibility)
 *   (이미 발급/배포 이전 앱 로그인 유저는 FE 단독 필터 불가라 BE eligibility로 판정 — 헤더 WEB)
 * - "쿠폰 받기": 앱 첫설치 혜택 발급(POST, 헤더 WEB)을 호출하고 ISSUED면 발급 완료 단계로 전환한다
 * - 재노출 쿨다운: 모달 닫기·앱 이동은 1일, "7일 동안 보지 않기"는 7일 (단일 키에 만료 시각 기록)
 * - "쿠폰 받기"(발급 성공)는 쿨다운을 시작하지 않는다 — 쿨다운 시 슬롯이 철회되어 발급 완료 모달이 즉시 닫히기 때문
 * - 다른 자동 노출 모달과의 중첩 방지는 autoShowModal 슬롯이 담당하며, 최하위 우선순위라 상위 모달이 닫힌 뒤 순차 노출된다
 */
export const useWebToAppInstallModal = () => {
  const { pathname } = usePathnameMethod();

  const [step, setStep] = useState(WebToAppModalStep.INSTALL);
  const [issuedCoupon, setIssuedCoupon] = useState<IssuedCoupon | null>(null);

  const { me } = useMeQuery();

  const isClientSide = useClientSide();
  const { isInCooldown, startCooldown } = useModalCooldown(
    LOCAL_STORAGE_KEYS.MODAL.WEB_TO_APP_INSTALL,
  );

  const isMobileDevice = isIOS || isAndroid;
  const isLoggedIn = !!me;
  const isVisiblePath = MAIN_SERVICE_PATHS.includes(pathname);

  // 노출 자격 조회 전제 조건 — 웹·모바일·로그인·노출 경로·쿨다운 외일 때만 BE에 자격을 확인한다
  const canCheckEligibility =
    isClientSide &&
    !isReactNative() &&
    isMobileDevice &&
    isLoggedIn &&
    isVisiblePath &&
    !isInCooldown;

  const { eligible } = useAppInstallBenefitEligibilityQuery({
    enabled: canCheckEligibility,
  });
  const { mutateAsync: issueAppInstallBenefit } =
    useIssueAppInstallBenefitMutation();

  const enabled = canCheckEligibility && eligible;

  const { isActive } = useAutoShowModalSlot({
    id: AutoShowModalId.WEB_TO_APP_INSTALL,
    enabled,
  });

  const handleClose = () => startCooldown(CLOSE_COOLDOWN_DURATION_MS);

  const handleNeverShowForSevenDays = () =>
    startCooldown(NEVER_SHOW_COOLDOWN_DURATION_MS);

  const handleClickIssueCoupon = async () => {
    try {
      const result = await issueAppInstallBenefit();

      new ReactNativeGtm().click_download_coupon({
        coupon_id: result.coupon?.couponId ?? null,
      });

      if (result.reason === CouponIssueReason.ISSUED && result.coupon) {
        setIssuedCoupon(result.coupon);
        setStep(WebToAppModalStep.COUPON_ISSUED);
        return;
      }

      // 발급 불가(이미 발급·대상 아님·마스터 이슈) — 완료 안내 없이 쿨다운 후 닫는다
      // TODO: [PRD-2930] 발급 실패 reason별 UX 확정 시 분기 보강
      startCooldown(CLOSE_COOLDOWN_DURATION_MS);
    } catch (error) {
      datadogLogs.logger.warn('앱 설치 쿠폰 발급 실패', { error });
      startCooldown(CLOSE_COOLDOWN_DURATION_MS);
    }
  };

  const handleClickUseInApp = () => {
    startCooldown(CLOSE_COOLDOWN_DURATION_MS);
    redirectApp();
  };

  // 슬롯이 비활성화되면(쿨다운·경로 이탈) 다음 노출이 설치 단계부터 시작하도록 초기화한다
  useEffect(() => {
    if (isActive) return;

    setStep(WebToAppModalStep.INSTALL);
    setIssuedCoupon(null);
  }, [isActive]);

  return {
    isOpen: isActive,
    step,
    issuedCoupon,
    handleClose,
    handleNeverShowForSevenDays,
    handleClickIssueCoupon,
    handleClickUseInApp,
  };
};
